/**
 * BaseDrawer - Enterprise Foundation Class
 *
 * Purpose: Reusable drawer pattern for cart, navigation, search, wishlist
 * Features: Focus trap, animations, accessibility, keyboard support
 *
 * DO NOT modify this file without testing thoroughly
 */

// Guard to prevent duplicate class declaration
if (typeof window.BaseDrawer === 'undefined') {
  class BaseDrawer extends HTMLElement {
  constructor() {
    super();

    // State management
    this.isDrawerOpen = false;
    this.previouslyFocusedElement = null;
    this.scrollPosition = 0;

    // Configuration (sensible defaults: most features ON by default)
    this.options = {
      position: this.getAttribute('position') || 'right', // left, right, top, bottom
      enableGestures: this.hasAttribute('enable-gestures'),
      closeOnBackdrop: this.getAttribute('close-on-backdrop') !== 'false',
      closeOnEscape: this.getAttribute('close-on-escape') !== 'false',
      lockBodyScroll: this.getAttribute('lock-body-scroll') !== 'false',
      focusTrap: this.getAttribute('focus-trap') !== 'false',
      hideShopifyChat: true,
      animation: {
        duration: 400,
        easing: 'cubic-bezier(0.4, 0, 0.2, 1)'
      }
    };

    // DOM elements (will be set in connectedCallback)
    this.overlay = null;
    this.panel = null;
    this.closeButton = null;
  }

  connectedCallback() {
    this.initializeElements();
    this.attachEventListeners();
  }

  disconnectedCallback() {
    this.cleanup();
  }

  /**
   * Initialize DOM elements
   */
  initializeElements() {
    this.overlay = this.querySelector('[data-drawer-overlay]') ||
                   this.querySelector('#CartDrawer-Overlay') ||
                   this.querySelector('.cart-drawer__overlay');

    this.panel = this.querySelector('[data-drawer-panel]') ||
                 this.querySelector('.drawer__inner');

    this.closeButton = this.querySelector('[data-drawer-close]') ||
                       this.querySelector('.drawer__close');
  }

  /**
   * Attach event listeners
   */
  attachEventListeners() {
    // Escape key
    if (this.options.closeOnEscape) {
      this.addEventListener('keyup', (evt) => {
        if (evt.code === 'Escape' && this.isDrawerOpen) {
          this.close();
        }
      });
    }

    // Backdrop click
    if (this.overlay && this.options.closeOnBackdrop) {
      this.overlay.addEventListener('click', () => this.close());
    }

    // Close button
    if (this.closeButton) {
      this.closeButton.addEventListener('click', () => this.close());
    }
  }

  /**
   * Open drawer
   * @param {HTMLElement} triggeredBy - Element that triggered the drawer
   */
  open(triggeredBy) {
    if (this.isDrawerOpen) return;

    // Store reference to triggering element for focus return
    if (triggeredBy) {
      this.previouslyFocusedElement = triggeredBy;
    }

    // Dispatch before-open event (can be cancelled)
    if (!this.dispatchDrawerEvent('before-open')) {
      return;
    }

    // Lock body scroll
    if (this.options.lockBodyScroll) {
      this.lockBodyScroll();
    }

    // Hide Shopify chat widget during drawer
    if (this.options.hideShopifyChat) {
      this.hideShopifyChat();
    }

    // Add animation classes
    setTimeout(() => {
      this.classList.add('animate', 'active');
    }, 10);

    // Set up focus trap after animation
    if (this.options.focusTrap) {
      this.addEventListener('transitionend', () => {
        this.setupFocusTrap();
      }, { once: true });
    }

    this.isDrawerOpen = true;

    // Dispatch after-open event
    this.dispatchDrawerEvent('after-open');
  }

  /**
   * Close drawer
   */
  close() {
    if (!this.isDrawerOpen) return;

    // Dispatch before-close event (can be cancelled)
    if (!this.dispatchDrawerEvent('before-close')) {
      return;
    }

    // Remove active class (triggers CSS animation)
    this.classList.remove('active');

    // Remove focus trap
    if (this.options.focusTrap && typeof removeTrapFocus === 'function') {
      removeTrapFocus(this.previouslyFocusedElement);
    }

    // Unlock body scroll
    if (this.options.lockBodyScroll) {
      this.unlockBodyScroll();
    }

    // Show Shopify chat widget again
    if (this.options.hideShopifyChat) {
      this.showShopifyChat();
    }

    this.isDrawerOpen = false;

    // Dispatch after-close event
    this.dispatchDrawerEvent('after-close');
  }

  /**
   * Lock body scroll
   */
  lockBodyScroll() {
    this.scrollPosition = window.pageYOffset;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${this.scrollPosition}px`;
    document.body.style.width = '100%';
    document.body.classList.add('overflow-hidden');
  }

  /**
   * Unlock body scroll
   */
  unlockBodyScroll() {
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    document.body.classList.remove('overflow-hidden');
    window.scrollTo(0, this.scrollPosition);
  }

  /**
   * Hide Shopify chat widget
   */
  hideShopifyChat() {
    const chatWidget = document.getElementById('shopify-chat');
    if (chatWidget) {
      chatWidget.style.display = 'none';
    }
  }

  /**
   * Show Shopify chat widget
   */
  showShopifyChat() {
    const chatWidget = document.getElementById('shopify-chat');
    if (chatWidget) {
      chatWidget.style.display = '';
    }
  }

  /**
   * Setup focus trap (uses global trapFocus from global.js)
   */
  setupFocusTrap() {
    if (typeof trapFocus !== 'function') {
      window.debugLogger.warn('trapFocus function not found. Include global.js');
      return;
    }

    const containerToTrapFocusOn = this.classList.contains('is-empty')
      ? this.querySelector('.drawer__inner-empty')
      : this.querySelector('#CartDrawer') || this.panel || this;

    const focusElement = this.panel || this.querySelector('.drawer__close');

    // Guard against null container
    if (!containerToTrapFocusOn) {
      window.debugLogger.warn('Focus trap container not found, skipping focus trap');
      return;
    }

    trapFocus(containerToTrapFocusOn, focusElement);
  }

  /**
   * Dispatch custom drawer events
   * @param {string} eventName
   * @returns {boolean} - true if not cancelled
   */
  dispatchDrawerEvent(eventName) {
    const event = new CustomEvent(`drawer:${eventName}`, {
      bubbles: true,
      cancelable: true,
      detail: {
        drawer: this,
        timestamp: Date.now()
      }
    });

    return this.dispatchEvent(event);
  }

  /**
   * Cleanup on disconnect
   */
  cleanup() {
    if (this.isDrawerOpen) {
      this.close();
    }
  }
  } // End of BaseDrawer class

  // Make BaseDrawer globally available
  window.BaseDrawer = BaseDrawer;
} // End of guard

// Export for use in other files
if (typeof module !== 'undefined' && module.exports) {
  module.exports = window.BaseDrawer;
}
